import React from 'react';
import { ReportData } from '../../types/report.types';
import { sortFindings } from '../../utils/reportSharedUtils';

interface RemediationRoadmapEditorV2Props {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
}

const RemediationRoadmapEditorV2: React.FC<RemediationRoadmapEditorV2Props> = ({ reportData, onDataChange }) => {
  const rawFindings = reportData.detailed_findings || [];
  const openFindings = rawFindings.filter(f => f.status?.toLowerCase() === 'open');
  const findings = sortFindings(openFindings);

  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;
  const roadmapOverrides = config.remediationRoadmap || {};

  const handleOverrideChange = (findingId: string, column: string, value: string) => {
    const currentFindingConfig = roadmapOverrides[findingId] || {};
    const updated = {
      ...roadmapOverrides,
      [findingId]: {
        ...currentFindingConfig,
        [column]: value
      }
    };
    onDataChange('finding_config', { ...config, remediationRoadmap: updated });
  };

  const getDefaults = (f: any) => {
    const sev = f.severity_category || 'Info';
    let priority = 'Info';
    let timeline = 'Best Effort';
    let owner = 'App Team';

    if (sev === 'Critical') {
      priority = 'Immediate';
      timeline = '7 Days';
    } else if (sev === 'High') {
      priority = 'Short term';
      timeline = '30 Days';
    } else if (sev === 'Medium') {
      priority = 'Medium term';
      timeline = '90 Days';
    } else if (sev === 'Low') {
      priority = 'Low';
      timeline = '180 Days';
    }

    const action = f.title ? `Remediate ${f.title}` : (f.fix ? f.fix.replace(/<[^>]*>/g, '').split(/[.!?]/)[0] + '.' : 'Remediate vulnerability');
    return { action, priority, timeline, owner };
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Remediation Roadmap Editor</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Customize action details, priorities, timelines, and owners for each open vulnerability</p>
          </div>
        </div>

        {findings.length === 0 ? (
          <div className="text-center py-8 bg-blue-50 rounded-lg border border-dashed border-blue-200">
            <p className="text-sm text-blue-700">No open findings in this report</p>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {findings.map((f, idx) => {
              const fid = f.report_id || f.id || `F-${idx + 1}`;
              const defaults = getDefaults(f);
              const overrides = roadmapOverrides[fid] || {};

              return (
                <div key={fid} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 border-b border-slate-200/50 pb-2">
                    <span className="text-xs font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{fid}</span>
                    <span className="text-xs font-bold text-slate-800">{f.title}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Remediation Action Required</label>
                      <input
                        type="text"
                        value={overrides.action !== undefined ? overrides.action : defaults.action}
                        onChange={(e) => handleOverrideChange(fid, 'action', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Priority</label>
                      <select
                        value={overrides.priority !== undefined ? overrides.priority : defaults.priority}
                        onChange={(e) => handleOverrideChange(fid, 'priority', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                      >
                        <option value="Immediate">Immediate</option>
                        <option value="Short term">Short term</option>
                        <option value="Medium term">Medium term</option>
                        <option value="Low">Low</option>
                        <option value="Info">Info</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Target Timeline</label>
                      <input
                        type="text"
                        value={overrides.timeline !== undefined ? overrides.timeline : defaults.timeline}
                        onChange={(e) => handleOverrideChange(fid, 'timeline', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Owner Assignment</label>
                      <input
                        type="text"
                        value={overrides.owner !== undefined ? overrides.owner : defaults.owner}
                        onChange={(e) => handleOverrideChange(fid, 'owner', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RemediationRoadmapEditorV2;
